import { deriveModuleFromPathname, formatModalStep } from "./bug-form";
import type { CaptureManifest, FixHints, FixHintFile } from "./types";

export function normalizeSourcePath(fileName: string): string {
  const webpackMatch = fileName.match(
    /(?:webpack[^/]*\/\.\/)?((?:src\/).+)$/
  );
  if (webpackMatch) {
    return webpackMatch[1];
  }

  const srcMatch = fileName.match(/[/\\]src[/\\](.+)$/);
  if (srcMatch) {
    return `src/${srcMatch[1].replace(/\\/g, "/")}`;
  }

  return fileName.replace(/\\/g, "/");
}

export function normalizeApiPath(url: string): {
  path: string;
  query?: string;
} {
  try {
    const parsed = new URL(url);
    return {
      path: parsed.pathname,
      query: parsed.search ? parsed.search.slice(1) : undefined,
    };
  } catch {
    const [pathPart, queryPart] = url.split("?");
    try {
      const relative = new URL(pathPart, "http://localhost");
      return {
        path: relative.pathname,
        query: queryPart,
      };
    } catch {
      return { path: pathPart, query: queryPart };
    }
  }
}

function addLikelyFile(
  files: FixHintFile[],
  seen: Set<string>,
  fileName: string | undefined,
  reason: string
): void {
  if (!fileName?.trim()) {
    return;
  }

  const path = normalizeSourcePath(fileName);
  if (seen.has(path)) {
    return;
  }

  seen.add(path);
  files.push({ path, reason });
}

export function buildFixHints(manifest: CaptureManifest): FixHints {
  const likelyFiles: FixHintFile[] = [];
  const seen = new Set<string>();

  addLikelyFile(
    likelyFiles,
    seen,
    manifest.react.primarySource?.fileName,
    "Primary React source at capture target"
  );

  for (const entry of manifest.react.componentStack) {
    addLikelyFile(
      likelyFiles,
      seen,
      entry.source?.fileName,
      `Component stack: ${entry.name}`
    );
  }

  if (manifest.surface?.surfaceId) {
    likelyFiles.push({
      path: `(grep surfaceId="${manifest.surface.surfaceId}")`,
      reason: "Registered modal surface",
    });
  }

  const routeSegments = manifest.pathname.split("/").filter(Boolean);
  if (routeSegments.length >= 2) {
    likelyFiles.push({
      path: `src/app/[branch_id]/${routeSegments.slice(1).join("/")}`,
      reason: "Route segment from pathname",
    });
  }

  const reproduction: string[] = [`Open ${manifest.url}`];

  if (manifest.pageEntity) {
    reproduction.push(
      `Page record: ${manifest.pageEntity.type} (${manifest.pageEntity.id})`
    );
  }

  if (manifest.surface) {
    reproduction.push(
      `Open modal: ${manifest.surface.title ?? manifest.surface.surfaceId}`
    );

    const step = formatModalStep(manifest.surface);
    if (step) {
      reproduction.push(`Navigate to: ${step}`);
    }

    if (manifest.surface.entityId) {
      reproduction.push(
        `Modal record: ${manifest.surface.entityType ?? "Entity"} (${manifest.surface.entityId})`
      );
    }
  }

  if (manifest.target.humanLabel?.trim()) {
    reproduction.push(`Interact with: ${manifest.target.humanLabel}`);
  } else if (
    manifest.target.tableCell?.columnHeader ||
    manifest.target.tableCell?.columnId ||
    manifest.target.tableCell?.rowId
  ) {
    const parts: string[] = [];
    const columnName =
      manifest.target.tableCell.columnHeader ||
      manifest.target.tableCell.columnId;
    if (columnName) {
      parts.push(`column "${columnName}"`);
    }
    if (manifest.target.tableCell.rowId) {
      parts.push(`row id ${manifest.target.tableCell.rowId}`);
    }
    reproduction.push(`Table cell: ${parts.join(", ")}`);
  } else {
    reproduction.push(`Interact with: ${manifest.target.selector}`);
  }

  const failedApiHints = manifest.failedRequests?.map((request) => {
    const normalized = normalizeApiPath(request.path ?? request.url);

    return {
      method: request.method,
      path: normalized.path,
      status: request.status,
      query: request.query ?? normalized.query,
      durationMs: request.durationMs,
    };
  });

  return {
    likelyFiles: likelyFiles.slice(0, 8),
    routeModule:
      manifest.surface?.module ?? deriveModuleFromPathname(manifest.pathname),
    reproduction,
    failedApiHints: failedApiHints?.length ? failedApiHints : undefined,
  };
}
